import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Hero } from "../components/Hero";
import { Marquee } from "../components/Marquee";
import { MediaCard } from "../components/MediaCard";
import { Lightbox } from "../components/Lightbox";
import { Reveal, SectionHead } from "../components/Reveal";
import { EmptyState } from "../components/EmptyState";
import { AdSlot } from "../components/Ads";
import { http, asList, MOOD } from "../lib/api";

const More = ({ to, testid }) => (
  <Link to={to} data-testid={testid} className="group flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-slate-300 hover:text-rose-400">
    Ver tudo <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </Link>
);

export default function Home() {
  const [videos, setVideos] = useState([]);
  const [popular, setPopular] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [cats, setCats] = useState([]);
  const [lb, setLb] = useState(-1);

  useEffect(() => {
    http.get("/media", { params: { type: "video", limit: 5 } }).then((r) => setVideos(asList(r))).catch(() => {});
    http.get("/media", { params: { type: "video", sort: "popular", limit: 6 } }).then((r) => setPopular(asList(r))).catch(() => {});
    http.get("/media", { params: { type: "photo", limit: 8 } }).then((r) => setPhotos(asList(r))).catch(() => {});
    http.get("/categories").then((r) => setCats(Array.isArray(r.data?.categories) ? r.data.categories : [])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <AdSlot placement="home" className="px-5 pt-16" />

      <section className="mx-auto max-w-[1440px] px-5 py-28 sm:px-10" data-testid="latest-videos-section">
        <Reveal><SectionHead eyebrow="01 — Lançamentos" title={<>Recém <span className="italic text-rose-500">chegados</span></>} action={<More to="/videos" testid="latest-see-all" />} /></Reveal>
        {videos.length === 0 ? (
          <EmptyState testid="videos-empty" text="Os primeiros vídeos estão a caminho." />
        ) : videos.length < 5 ? (
          <div className="grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-testid="video-bento">
            {videos.map((v, i) => <Reveal key={v.id} delay={i * 0.07}><MediaCard item={v} /></Reveal>)}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:auto-rows-[240px] lg:grid-cols-4" data-testid="video-bento">
            {videos.map((v, i) => (
              <Reveal key={v.id} delay={i * 0.07} className={i === 0 ? "sm:col-span-2 lg:row-span-2" : "lg:row-span-1"}>
                <MediaCard item={v} big={i === 0} className={i === 0 ? "" : "lg:[&>div]:aspect-auto lg:[&>div]:h-[180px]"} />
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {popular.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-5 pb-28 sm:px-10" data-testid="popular-section">
          <Reveal><SectionHead eyebrow="02 — Em alta" title={<>Os mais <span className="italic text-rose-500">assistidos</span></>} action={<More to="/em-alta" testid="popular-see-all" />} /></Reveal>
          <div className="grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {popular.map((v, i) => (
              <Reveal key={v.id} delay={(i % 3) * 0.08}>
                <div className="relative">
                  <span className="font-serif-d pointer-events-none absolute -left-2 -top-8 z-10 text-6xl italic text-rose-600/80">{String(i + 1).padStart(2, "0")}</span>
                  <MediaCard item={v} />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="relative overflow-hidden border-y border-white/5 bg-[#0b0a0e] py-28" data-testid="photos-section">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-10">
          <Reveal><SectionHead eyebrow="03 — Ensaios" title={<>Galeria de <span className="italic text-rose-500">fotos</span></>} action={<More to="/fotos" testid="photos-see-all" />} /></Reveal>
          {photos.length === 0 ? (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {MOOD.slice(0, 4).map((m, i) => (
                <Reveal key={m} delay={i * 0.08}>
                  <div className={`relative overflow-hidden ${i % 2 ? "aspect-[3/4] md:mt-16" : "aspect-[3/4]"}`}>
                    <img src={m} alt="" className="h-full w-full object-cover opacity-60 grayscale transition-[filter,opacity] duration-700 hover:opacity-90 hover:grayscale-0" />
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="home-photo-grid">
              {photos.map((p, i) => (
                <Reveal key={p.id} delay={(i % 4) * 0.06} className={i % 2 ? "md:mt-16" : ""}>
                  <MediaCard item={p} onOpen={() => setLb(i)} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <AdSlot placement="home" className="px-5 pt-20" />

      {cats.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-5 py-28 sm:px-10" data-testid="categories-section">
          <Reveal><SectionHead eyebrow="04 — Explorar" title="Categorias" /></Reveal>
          <div className="flex flex-wrap gap-x-10 gap-y-4">
            {cats.map((c, i) => (
              <Reveal key={c} delay={i * 0.04}>
                <Link to={`/videos?category=${encodeURIComponent(c)}`} data-testid={`category-link-${c}`} className="font-serif-d text-3xl text-slate-300 transition-colors hover:italic hover:text-rose-500 sm:text-5xl">
                  {c}<span className="text-rose-600">.</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {lb >= 0 && <Lightbox items={photos} index={lb} onIndex={setLb} onClose={() => setLb(-1)} />}
    </div>
  );
}
